/**
 * « Vous êtes plusieurs en ligne » (12/10/2026) : discret, réservé au bureau.
 * Les comptes qui ont accès aux vues du bureau (`office_access`, décidé par les
 * droits du rôle) voient les visages des autres qui ont l'application ouverte ;
 * un appui affiche leurs noms. Rien ne s'affiche quand on est seul, ni pour un
 * compte de terrain, ni pendant une session d'assistance. Le signal part toutes les minutes, seulement onglet visible.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../core/api.js';
import { useAuth } from '../core/auth.js';
import { UserAvatar } from './UserAvatar.js';

interface Presence {
  online: { id: string; full_name: string; avatar_url: string | null }[];
}

/** `barre` : les visages seuls (barre du haut) ; `ligne` : avec qui est en ligne, écrit (Menu). */
export function PresenceIndicator({ variant = 'barre' }: { variant?: 'barre' | 'ligne' }): ReactNode {
  const { me } = useAuth();
  const office = me?.office_access === true;
  const { data } = useQuery({
    queryKey: ['presence', me?.id],
    queryFn: () => api<Presence>('/presence', { method: 'POST' }),
    enabled: office,
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: false,
  });
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event: Event) => {
      if (
        event instanceof KeyboardEvent
          ? event.key === 'Escape'
          : !root.current?.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  const others = data?.online ?? [];
  if (!office || others.length === 0) return null;
  const label =
    others.length === 1
      ? `${others[0]!.full_name} est aussi en ligne`
      : `${others.length} collègues du bureau en ligne`;
  return (
    <span ref={root} className={variant === 'ligne' ? 'presence presence--ligne' : 'presence'}>
      <button
        type="button"
        className="presence__button"
        aria-label={label}
        aria-expanded={open}
        title={label}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="presence__faces">
          {others.slice(0, 3).map((user) => (
            <UserAvatar
              key={user.id}
              fullName={user.full_name}
              avatarUrl={user.avatar_url}
              className="avatar presence__face"
            />
          ))}
        </span>
        {others.length > 3 ? <span className="presence__more">+{others.length - 3}</span> : null}
        <span className="presence__dot" aria-hidden="true" />
        {variant === 'ligne' ? <span aria-hidden="true">{label}</span> : null}
      </button>
      {open ? (
        <span className="presence__panel" role="status">
          <strong>En ligne au bureau</strong>
          <ul>
            {others.map((user) => (
              <li key={user.id}>{user.full_name}</li>
            ))}
          </ul>
        </span>
      ) : null}
    </span>
  );
}
